/**
 * api/observability.js — Observabilidade de Agentes (somente leitura).
 *
 *   GET /observability/agents                → `resumo` do período + um item por agente
 *   GET /observability/agents/{agent_name}   → série, quebras, portão por versão de prompt,
 *                                              loops por projeto, erros por tipo e modelos
 *   GET /observability/executions/{id}       → linha do tempo da execução
 *   GET /observability/invocations           → listagem paginada, com o `total` do filtro
 *   GET /observability/filters               → agentes e etapas distintos do período
 *   GET /observability/data-quality          → lacunas do registro × Wallet
 *
 * Permissões (a API decide): `agent_catalog:view` abre; valores de custo só com
 * `budget_costs:view` — sem ela os custos vêm `null` e `custos_visiveis=false`;
 * `data-quality` exige `budget_costs:view` inteiro (403).
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useApi } from './client';

/** Teto de página aceito pela API (acima disso ela responde 422, não corta). */
export const MAX_POR_PAGINA = 500;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** O texto é um UUID? (a API recusa com 422 o que não for.) */
export function ehUuid(valor) {
  return UUID.test(String(valor || '').trim());
}

/** Query string só com os parâmetros preenchidos. */
export function montarQuery(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    const s = String(v).trim();
    if (s !== '') q.set(k, s);
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function agentesUrl({ de, ate, projectId } = {}) {
  return `/observability/agents${montarQuery({ de, ate, project_id: projectId })}`;
}

export function agenteUrl(nome, { de, ate, projectId } = {}) {
  return `/observability/agents/${encodeURIComponent(nome)}${montarQuery({ de, ate, project_id: projectId })}`;
}

export function execucaoUrl(id) {
  return `/observability/executions/${encodeURIComponent(String(id).trim())}`;
}

/**
 * `filtros` usa os nomes da API (agent_name, project_id, etapa, tipo_de_rodada, status,
 * canal…). `agent_name`/`etapa` são exatos; `agente_contem`/`etapa_contem` filtram por
 * trecho, sem diferenciar maiúsculas.
 */
export function invocacoesUrl(filtros = {}, { limit = 50, offset = 0 } = {}) {
  return `/observability/invocations${montarQuery({ ...filtros, limit, offset })}`;
}

export function filtrosUrl({ de, ate, projectId } = {}) {
  return `/observability/filters${montarQuery({ de, ate, project_id: projectId })}`;
}

/**
 * Texto digitado num campo com seletor → parâmetro da API: valor que existe na lista vai
 * como filtro EXATO; qualquer outro texto vai como "contém".
 */
export function filtroExatoOuContem(texto, valores = [], { exato, contem }) {
  const t = String(texto || '').trim();
  if (!t) return {};
  return valores.includes(t) ? { [exato]: t } : { [contem]: t };
}

export function qualidadeUrl({ de, ate } = {}) {
  return `/observability/data-quality${montarQuery({ de, ate })}`;
}

/** Erro do cliente (403/404/422) não melhora tentando de novo. */
function tentarDeNovo(tentativas, erro) {
  const s = erro?.status;
  if (s >= 400 && s < 500) return false;
  return tentativas < 2;
}

export function useObsAgentes(filtro = {}) {
  const api = useApi();
  const url = agentesUrl(filtro);
  return useQuery({
    queryKey: ['observability', 'agents', url],
    queryFn: () => api.get(url),
    staleTime: 30_000,
    retry: tentarDeNovo,
  });
}

export function useObsAgente(nome, filtro = {}) {
  const api = useApi();
  const url = nome ? agenteUrl(nome, filtro) : null;
  return useQuery({
    queryKey: ['observability', 'agent', url],
    queryFn: () => api.get(url),
    enabled: !!url,
    staleTime: 30_000,
    retry: tentarDeNovo,
  });
}

export function useObsExecucao(id) {
  const api = useApi();
  const valido = ehUuid(id);
  return useQuery({
    queryKey: ['observability', 'execution', valido ? String(id).trim().toLowerCase() : null],
    queryFn: () => api.get(execucaoUrl(id)),
    enabled: valido,
    staleTime: 15_000,
    retry: tentarDeNovo,
  });
}

export function useObsInvocacoes(filtros = {}, pagina = {}, { enabled = true } = {}) {
  const api = useApi();
  const url = invocacoesUrl(filtros, pagina);
  return useQuery({
    queryKey: ['observability', 'invocations', url],
    queryFn: () => api.get(url),
    enabled,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
    retry: tentarDeNovo,
  });
}

/** Agentes e etapas com invocação no período — alimenta os seletores da listagem. */
export function useObsFiltros(filtro = {}) {
  const api = useApi();
  const url = filtrosUrl(filtro);
  return useQuery({
    queryKey: ['observability', 'filters', url],
    queryFn: () => api.get(url),
    staleTime: 30_000,
    retry: tentarDeNovo,
  });
}

export function useObsQualidade(filtro = {}) {
  const api = useApi();
  const url = qualidadeUrl(filtro);
  return useQuery({
    queryKey: ['observability', 'data-quality', url],
    queryFn: () => api.get(url),
    staleTime: 30_000,
    retry: tentarDeNovo,
  });
}
